import { lmaReduxStore } from '@redux/store';
import { IBriefing } from '../interfaces/IBriefing';

/**
 * Same-origin path proxied by nginx to lma-scheduler-service (see docker/nginx.vh.default.conf).
 * Using the UI's own origin avoids exposing the internal scheduler through a public ingress.
 */
const BRIEFING_API_BASE = '/briefing-api';

/**
 * Fetches the stored briefings for a day. The `token` header mirrors the other lma-ui REST calls
 * (AlertsService); the scheduler ignores it for the stage validation, but sending it keeps the
 * call ready for the Keycloak-scoped production read path.
 */
export async function fetchBriefings(date?: string, orgKeyPrefix = 'oz:'): Promise<IBriefing[]> {
  const params = new URLSearchParams();
  if (date) {
    params.set('date', date);
  }
  if (orgKeyPrefix) {
    params.set('orgKeyPrefix', orgKeyPrefix);
  }

  const url = `${BRIEFING_API_BASE}/briefings?${params.toString()}`;
  const token = lmaReduxStore.getState().auth?.tokenId;
  const response = await fetch(url, {
    headers: token ? { token } : {},
  });

  if (!response.ok) {
    throw new Error(`Briefing API returned ${response.status}`);
  }
  return response.json();
}

/** A reader's verdict on one finding. `useful` may be omitted when only flagging irrelevance. */
export type BriefingFeedback = {
  date: string;
  orgKey: string;
  signalType: string;
  kpiId?: string;
  useful?: boolean;
  notRelevant?: boolean;
  comment?: string;
};

/**
 * Records whether a finding was useful. Without this nothing could tell which signals are worth the
 * screen space, so both the thresholds and the prompts could only be tuned by guesswork.
 */
export async function sendBriefingFeedback(feedback: BriefingFeedback): Promise<void> {
  const token = lmaReduxStore.getState().auth?.tokenId;
  const response = await fetch(`${BRIEFING_API_BASE}/briefings/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { token } : {}),
    },
    body: JSON.stringify(feedback),
  });
  if (!response.ok) {
    throw new Error(`Briefing API returned ${response.status}`);
  }
}
